import { Injectable } from '@angular/core';
import type { TranslationObject } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, shareReplay } from 'rxjs';

/**
 * ngx-translate's HTTP loader, plus `prefetch`: a language fetched ahead of time is handed to the
 * next `getTranslation` call for it, so switching to that language is as instant as when it was
 * already loaded. Prefetching does not touch the translations in use, so nothing on the page
 * reacts to it.
 */
@Injectable()
export class PrefetchingTranslateLoader extends TranslateHttpLoader {
  private readonly prefetched = new Map<string, Observable<TranslationObject>>();

  override getTranslation(lang: string): Observable<TranslationObject> {
    const prefetched = this.prefetched.get(lang);
    if (!prefetched) return super.getTranslation(lang);
    this.prefetched.delete(lang);
    return prefetched;
  }

  prefetch(lang: string): void {
    if (this.prefetched.has(lang)) return;
    const load = super.getTranslation(lang).pipe(shareReplay(1));
    this.prefetched.set(lang, load);
    // A failed prefetch is forgotten, so the switch loads the language itself.
    load.subscribe({ error: () => this.prefetched.delete(lang) });
  }
}
